/**
 * HINT-mode time-of-day bands — FE mirror of the backend's single source of
 * truth (`ergonai-gql-api` → `helpers/availability/hintBands.ts`).
 *
 * Ranges are right-open `[start, end)` in local minutes since midnight;
 * NIGHT wraps past midnight (its post-midnight portion belongs to the booked
 * day + 1). Bands are indicative placeholders — the professional confirms the
 * exact time at approval. The token list is confirmed definitive by the PO;
 * unknown tokens degrade gracefully (checks are skipped, mirroring the
 * backend's forward-compat rule: never reject a token this module does not
 * know).
 */

export interface HintBand {
  /** `TimeOfDayHint` token (e.g. "MORNING"). */
  token: string;
  /** Inclusive band start, local minutes since midnight. */
  startMinutes: number;
  /** Exclusive band end, local minutes since midnight (right-open). */
  endMinutes: number;
  /** True when the band crosses midnight (i.e. `start > end`). */
  wrapsMidnight: boolean;
  /**
   * Representative instant (local minutes) the backend uses for the booked
   * placeholder calendar event — where a RESERVED band event shows up.
   */
  sampleMinutes: number;
}

const min = (hours: number, minutes = 0): number => hours * 60 + minutes;

export const HINT_BANDS: Record<string, HintBand> = {
  NIGHT: {
    token: "NIGHT",
    startMinutes: min(22),
    endMinutes: min(4),
    wrapsMidnight: true,
    sampleMinutes: min(22),
  },
  EARLY_MORNING: {
    token: "EARLY_MORNING",
    startMinutes: min(4),
    endMinutes: min(8),
    wrapsMidnight: false,
    sampleMinutes: min(6),
  },
  MORNING: {
    token: "MORNING",
    startMinutes: min(8),
    endMinutes: min(12),
    wrapsMidnight: false,
    sampleMinutes: min(10),
  },
  AFTERNOON: {
    token: "AFTERNOON",
    startMinutes: min(12),
    endMinutes: min(17),
    wrapsMidnight: false,
    sampleMinutes: min(15),
  },
  EVENING: {
    token: "EVENING",
    startMinutes: min(17),
    endMinutes: min(22),
    wrapsMidnight: false,
    sampleMinutes: min(19),
  },
};

/** Band for a token, or `null` when the token is unknown (forward compat). */
export const getHintBand = (token: string): HintBand | null =>
  HINT_BANDS[token] ?? null;

/**
 * Is `localMinutes` inside the token's band (right-open, wrap-aware)?
 * Returns `null` for an unknown token so callers can skip the range check.
 */
export const isWithinHintBand = (
  token: string,
  localMinutes: number,
): boolean | null => {
  const band = getHintBand(token);
  if (!band) return null;
  return band.wrapsMidnight
    ? localMinutes >= band.startMinutes || localMinutes < band.endMinutes
    : localMinutes >= band.startMinutes && localMinutes < band.endMinutes;
};

/**
 * Calendar-day offset the confirmed instant carries relative to the booked
 * day. For a wrap band, the post-midnight portion `[00:00, end)` belongs to
 * `bookedDay + 1`; everything else stays on the booked day.
 */
export const expectedBookedDayDelta = (
  token: string,
  localMinutes: number,
): number => {
  const band = getHintBand(token);
  return band?.wrapsMidnight && localMinutes < band.endMinutes ? 1 : 0;
};

/**
 * In-band time presets (minutes of day) in chronological visit order —
 * wrap bands walk pre-midnight marks first, then post-midnight ones.
 * Empty for an unknown token.
 */
export const hintBandPresetMinutes = (
  token: string,
  stepMinutes = 30,
): number[] => {
  const band = getHintBand(token);
  if (!band || stepMinutes <= 0) return [];
  const end = band.wrapsMidnight ? band.endMinutes + 24 * 60 : band.endMinutes;
  const presets: number[] = [];
  for (let m = band.startMinutes; m < end; m += stepMinutes) {
    presets.push(m % (24 * 60));
  }
  return presets;
};

/** Format minutes-of-day as a "HH:mm" label. */
export const minutesToTimeLabel = (minutes: number): string => {
  const h = Math.floor(minutes / 60) % 24;
  const m = minutes % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};

/** Parse a "HH:mm" label into minutes of day; `null` for anything else. */
export const timeLabelToMinutes = (label: string): number | null => {
  const match = /^(\d{2}):(\d{2})$/.exec(label);
  if (!match) return null;
  const h = Number(match[1]);
  const m = Number(match[2]);
  if (h > 23 || m > 59) return null;
  return h * 60 + m;
};

/**
 * Indicative "HH:mm – HH:mm" range label for a band (locale-invariant, same
 * shape the consumer band list shows). `null` for an unknown token.
 */
export const formatHintBandRange = (token: string): string | null => {
  const band = getHintBand(token);
  if (!band) return null;
  return `${minutesToTimeLabel(band.startMinutes)} – ${minutesToTimeLabel(band.endMinutes)}`;
};
